/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Autocomplete } from '@mic3/platform-ui';

import { bind, debounce } from 'app/utils/decorators/decoratorUtils';
import { isValiduuid } from 'app/utils/string/string-utils';
import { shallowEquals } from 'app/utils/utils';

import ResolveAvatarTypeaheadWrapper from 'app/components/organisms/Form/Typeahead/abstract/ResolveAvatarTypeaheadWrapper';
import { loadBroadcastTypeahead } from 'store/actions/broadcasts/broadcastsActions';

/**
 * Select one or more groups using lazy loading.
 */
class BroadcastTypeahead extends PureComponent {
    static propTypes = {
        loadOptions: PropTypes.func.isRequired,
        options: PropTypes.arrayOf(PropTypes.object),
        isLoading: PropTypes.bool,
        filterBy: PropTypes.arrayOf(PropTypes.object),
    };

    state = { options: [], isLoading: false };

    componentDidUpdate(prevProps) {
        if (!shallowEquals(prevProps, this.props)) {
            this.setState({ options: [] });
        }
    }

    @bind
    optionTemplate({ message, id }) {
        return {
            label: `${message || 'Name not available'} (${id ? id.slice(0, 8) : 'Id not available'})`,
        };
    }

    @bind
    @debounce()
    suggest(event) {
        this.setState({ isLoading: true });
        const { valueField, excludeBy, value, loadOptions, filterBy, orderBy = [{ field: 'message', direction: 'asc nulls last' }] } = this.props;
        const excludes = [...(excludeBy || [])];
        const filters = [];
        const query = event.target.value;
        if (query) {
            if (isValiduuid(query)) {
                filters.push({
                    or: [
                        { field: 'message', op: 'contains', value: query },
                        { field: 'id', op: 'startsWith', value: query, cast: 'text' },
                    ],
                });
            } else {
                filters.push({ field: 'message', op: 'contains', value: query });
            }
        }
        if (filterBy) {
            if (typeof filterBy === 'function') {
                filters.push(...filterBy(value));
            } else if (filterBy.length) {
                filters.push(...filterBy);
            }
        }
        if (value) {
            const val = Array.isArray(value) ? value : [value];
            let orVal = [];
            if (valueField) {
                orVal = val.map(fieldVal => ({ field: valueField, op: '=', value: fieldVal }));
            } else {
                orVal = val.map(({ id }) => ({ field: 'id', op: '=', value: id }));
            }
            if (orVal.length) {
                excludes.push({ or: orVal });
            }
        }

        loadOptions({
            startIndex: 0,
            stopIndex: 50,
            filterBy: filters,
            excludeBy: excludes,
            orderBy: orderBy,
        })
            .then(options => {
                this.setState({ options, isLoading: false });
            })
            .catch(error => {
                this.setState({ options: [], isLoading: false });
            });
    }

    render() {
        const { excludeBy, loadOptions, filterBy, placeholder, ...typeaheadProps } = this.props; // eslint-disable-line no-unused-vars
        const { options, isLoading } = this.state;
        return (
            <Autocomplete
                {...typeaheadProps}
                VirtualListProps={{
                    itemSize: 60,
                }}
                options={options}
                isLoading={isLoading}
                optionTemplate={this.optionTemplate}
                suggest={this.suggest}
                placeholder={placeholder || 'Search for a Broadcast...'}
            />
        );
    }
}

const BroadcastTypeaheadWrapped = props => {
    return <ResolveAvatarTypeaheadWrapper {...props} Typeahead={BroadcastTypeahead} entityType="broadcast" />;
};

export default connect(null, { loadOptions: loadBroadcastTypeahead })(BroadcastTypeaheadWrapped);
